import { db } from './firebase/config';
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { isUserAdmin } from '../firebase/services/userService';
import firestoreListenerManager from './firestoreListenerManager';

/**
 * Service unifié pour la gestion des meetings avec leurs transcriptions et résumés
 * Optimisé avec onSnapshot pour le temps réel et cache intelligent
 */
class MeetingsDataService {
  constructor() {
    this.cache = {
      meetings: new Map(),
      transcriptions: new Map(),
      summaries: new Map(),
      userIsAdmin: null,
      currentUserId: null
    };
    this.listeners = new Map();
    this.callbacks = new Map(); // Stocker les callbacks pour permettre le restart après refresh token
    this.isInitialized = false;
  }

  /**
   * Initialise le service avec l'utilisateur courant
   */
  async initialize() {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (!user) {
        throw new Error('Utilisateur non authentifié');
      }

      this.cache.currentUserId = user.uid;
      
      // Vérifier si l'utilisateur est admin (avec cache)
      if (this.cache.userIsAdmin === null) {
        this.cache.userIsAdmin = await isUserAdmin(user.uid);
      }
      
      this.isInitialized = true;
      return true;
    } catch (error) {

      throw error;
    }
  }

  /**
   * Vérifie si l'utilisateur peut accéder à un meeting
   */
  canAccessMeeting(meeting) {
    if (!this.cache.currentUserId) return false;
    
    // Propriétaire
    if (meeting.userId === this.cache.currentUserId) return true;
    
    // Meeting confidentiel : UNIQUEMENT le propriétaire
    if (meeting.isConfidential === true) {
      return meeting.userId === this.cache.currentUserId;
    }
    
    // Meeting normal : propriétaire, partagé avec, ou admin
    if (meeting.sharedWith && meeting.sharedWith.includes(this.cache.currentUserId)) return true;
    if (this.cache.userIsAdmin) return true;
    
    return false;
  }

  /**
   * Démarre l'écoute en temps réel de tous les meetings accessibles
   */
  async startMeetingsListener(callback) {
    if (!this.isInitialized) {
      await this.initialize();
    }

    const meetingsRef = collection(db, 'meetings');
    const q = query(meetingsRef, orderBy('createdAt', 'desc'));
    
    // Stocker le callback pour permettre le restart après refresh
    this.callbacks.set('meetings', callback);

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      try {
        const allMeetings = [];

        querySnapshot.forEach(doc => {
          const meeting = { id: doc.id, ...doc.data() };

          // Filtrer selon les droits d'accès
          if (this.canAccessMeeting(meeting)) {
            // Convertir les Timestamps en Date
            if (meeting.createdAt?.toDate) meeting.createdAt = meeting.createdAt.toDate();
            if (meeting.updatedAt?.toDate) meeting.updatedAt = meeting.updatedAt.toDate();

            this.cache.meetings.set(meeting.id, meeting);
            allMeetings.push(meeting);
          }
        });

        callback(allMeetings);
      } catch (error) {
        callback([]);
      }
    }, (error) => {
      console.error('❌ Erreur de connexion Firestore (meetings):', error);

      // 🔥 CORRECTION CORS : Détecter les erreurs d'authentification
      if (error.code === 'permission-denied' ||
          error.message?.includes('CORS') ||
          error.message?.includes('access control') ||
          error.message?.includes('unauthenticated')) {
        // Signaler au système qu'un refresh est nécessaire
        window.dispatchEvent(new CustomEvent('firestore-auth-error', {
          detail: { error, source: 'meetings-listener' }
        }));
      }

      callback([]);
    });

    this.listeners.set('meetings', unsubscribe);

    // 🔥 CORRECTION CORS : Enregistrer le listener avec le manager pour permettre le restart
    const restartFn = () => {
      const storedCallback = this.callbacks.get('meetings');
      if (storedCallback) {
        return this.startMeetingsListener(storedCallback);
      }
      return null;
    };
    firestoreListenerManager.registerListener('meetings-listener', unsubscribe, restartFn);

    return unsubscribe;
  }

  /**
   * Démarre l'écoute en temps réel de toutes les transcriptions accessibles
   */
  async startTranscriptionsListener(callback) {
    if (!this.isInitialized) {
      await this.initialize();
    }

    const transcriptionsRef = collection(db, 'transcriptions');
    
    // Si admin, écouter toutes les transcriptions, sinon filtrer par userId
    const q = this.cache.userIsAdmin 
      ? query(transcriptionsRef, orderBy('createdAt', 'desc'))
      : query(transcriptionsRef, 
          where('userId', '==', this.cache.currentUserId),
          orderBy('createdAt', 'desc')
        );
    
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      try {
        const transcriptions = [];
        
        querySnapshot.forEach(doc => {
          const transcription = { id: doc.id, ...doc.data() };
          
          // Vérification supplémentaire pour les meetings confidentiels
          const meeting = this.cache.meetings.get(transcription.meetingId);
          if (meeting && meeting.isConfidential === true && !this.cache.userIsAdmin) {
            // Pour les meetings confidentiels, seul le propriétaire peut voir
            if (transcription.userId !== this.cache.currentUserId) {
              return; // Skip cette transcription
            }
          }
          
          if (transcription.createdAt?.toDate) transcription.createdAt = transcription.createdAt.toDate();
          if (transcription.updatedAt?.toDate) transcription.updatedAt = transcription.updatedAt.toDate();
          
          this.cache.transcriptions.set(transcription.id, transcription);
          transcriptions.push(transcription);
        });
        
        callback(transcriptions);
      } catch (error) {

        callback([]);
      }
    }, (error) => {
      console.error('❌ Erreur de connexion Firestore (transcriptions):', error);
      callback([]);
    });

    this.listeners.set('transcriptions', unsubscribe);
    return unsubscribe;
  }

  /**
   * Démarre l'écoute en temps réel de tous les résumés accessibles
   */
  async startSummariesListener(callback) {
    if (!this.isInitialized) {
      await this.initialize();
    }

    const summariesRef = collection(db, 'summaries');
    
    // Si admin, écouter tous les résumés, sinon filtrer par userId
    const q = this.cache.userIsAdmin 
      ? query(summariesRef, orderBy('createdAt', 'desc'))
      : query(summariesRef, 
          where('userId', '==', this.cache.currentUserId),
          orderBy('createdAt', 'desc')
        );
    
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      try {
        const summaries = [];
        
        querySnapshot.forEach(doc => {
          const summary = { id: doc.id, ...doc.data() };
          
          // Vérification supplémentaire pour les meetings confidentiels via transcription
          const transcription = this.cache.transcriptions.get(summary.transcriptionId);
          if (transcription) {
            const meeting = this.cache.meetings.get(transcription.meetingId);
            if (meeting && meeting.isConfidential === true && !this.cache.userIsAdmin) {
              // Pour les meetings confidentiels, seul le propriétaire peut voir
              if (summary.userId !== this.cache.currentUserId) {
                return; // Skip ce résumé
              }
            }
          }
          
          if (summary.createdAt?.toDate) summary.createdAt = summary.createdAt.toDate();
          if (summary.updatedAt?.toDate) summary.updatedAt = summary.updatedAt.toDate();
          
          this.cache.summaries.set(summary.id, summary);
          summaries.push(summary);
        });
        
        callback(summaries);
      } catch (error) {

        callback([]);
      }
    }, (error) => {
      console.error('❌ Erreur de connexion Firestore (summaries):', error);
      callback([]);
    });

    this.listeners.set('summaries', unsubscribe);
    return unsubscribe;
  }

  /**
   * Démarre l'écoute complète avec données enrichies
   * Retourne les meetings avec leurs transcriptions et résumés liés
   */
  async startCompleteDataListener(callback) {
    if (!this.isInitialized) {
      await this.initialize();
    }

    let meetingsData = [];
    let transcriptionsData = [];
    let summariesData = [];
    let listenersCount = 0;
    const totalListeners = 3;

    const updateCompleteData = () => {
      if (listenersCount < totalListeners) return;

      try {
        // Enrichir les meetings avec leurs transcriptions et résumés
        const enrichedMeetings = meetingsData.map(meeting => {
          const transcription = transcriptionsData.find(t => t.meetingId === meeting.id);
          const summary = transcription ? summariesData.find(s => s.transcriptionId === transcription.id) : null;
          
          return {
            ...meeting,
            transcription: transcription || null,
            summary: summary || null,
            hasTranscription: !!transcription,
            hasSummary: !!summary
          };
        });

        callback(enrichedMeetings);
      } catch (error) {

        callback([]);
      }
    };

    // Démarrer les 3 listeners
    await this.startMeetingsListener((meetings) => {
      meetingsData = meetings;
      listenersCount = Math.max(listenersCount, 1);
      updateCompleteData();
    });

    await this.startTranscriptionsListener((transcriptions) => {
      transcriptionsData = transcriptions;
      listenersCount = Math.max(listenersCount, 2);
      updateCompleteData();
    });

    await this.startSummariesListener((summaries) => {
      summariesData = summaries;
      listenersCount = Math.max(listenersCount, 3);
      updateCompleteData();
    });

    // Retourner une fonction de nettoyage
    return () => {
      this.stopAllListeners();
    };
  }

  /**
   * Arrête tous les listeners actifs
   */
  stopAllListeners() {
    this.listeners.forEach((unsubscribe, key) => {
      try {
        unsubscribe();
      } catch (error) {

      }
    });
    this.listeners.clear();
  }

  /**
   * Récupère un meeting depuis le cache avec ses données liées
   */
  getMeetingWithData(meetingId) {
    const meeting = this.cache.meetings.get(meetingId);
    if (!meeting) return null;

    const transcription = Array.from(this.cache.transcriptions.values())
      .find(t => t.meetingId === meetingId);
    
    const summary = transcription ? this.cache.summaries.get(transcription.id) : null;

    return {
      ...meeting,
      transcription: transcription || null,
      summary: summary || null,
      hasTranscription: !!transcription,
      hasSummary: !!summary
    };
  }

  /**
   * Rafraîchit le statut admin de l'utilisateur
   */
  async refreshAdminStatus() {
    if (this.cache.currentUserId) {
      const oldStatus = this.cache.userIsAdmin;
      this.cache.userIsAdmin = await isUserAdmin(this.cache.currentUserId);
      
      if (oldStatus !== this.cache.userIsAdmin) {
        return true; // Indiquer qu'il y a eu un changement
      }
    }
    return false;
  }

  /**
   * Nettoyage complet du service
   */
  cleanup() {
    this.stopAllListeners();
    this.cache.meetings.clear();
    this.cache.transcriptions.clear();
    this.cache.summaries.clear();
    this.cache.userIsAdmin = null;
    this.cache.currentUserId = null;
    this.isInitialized = false;
  }
}

// Export d'une instance singleton
export default new MeetingsDataService(); 